import type { AnyRoute } from "@tanstack/react-router";
import type { Scope } from "@backstory/protocol";

/**
 * Feature registry: each feature module (features/<name>/index.ts) registers its
 * routes and navigation entries here, and router.tsx / Shell.tsx consume the
 * lists. This lets independent workstreams add pages without editing shared files.
 */
export interface NavEntry {
  label: string;
  /** Route path template, e.g. "/issues" or "/orgs/$org/notifications". */
  to: string;
  /** Which nav group the entry belongs to. */
  section: "main" | "org" | "user";
  /** Optional sort order within the section (lower first). */
  order?: number;
  /** Requires an org context to render (params { org }). */
  needsOrg?: boolean;
  /** Shown only to roles holding this scope (see auth/roles.ts). Admins see everything. */
  scope?: Scope;
  /** Shown only when the organization's plan includes this feature, unless `upgrade` is set. */
  feature?: string;
  /** Keep the item visible when the plan lacks `feature`. The text names the plans that include it, such as "Team and above". */
  upgrade?: string;
}

export type RouteFactory = (appRoute: AnyRoute) => AnyRoute[];

const routeFactories: RouteFactory[] = [];
const navEntries: NavEntry[] = [];

export function registerFeature(f: { routes?: RouteFactory; nav?: NavEntry[] }): void {
  if (f.routes) routeFactories.push(f.routes);
  if (f.nav) navEntries.push(...f.nav);
}

export function featureRoutes(appRoute: AnyRoute): AnyRoute[] {
  return routeFactories.flatMap((f) => f(appRoute));
}

export function featureNav(section: NavEntry["section"]): NavEntry[] {
  return navEntries.filter((n) => n.section === section).sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
}
